/**
 * EntitlementGate — Wrapper that renders children only when the
 * required module entitlement is enabled; otherwise shows ModuleNotEnabled.
 *
 * Usage:
 *   <EntitlementGate module="scheduler">...</EntitlementGate>
 *   <EntitlementGate module="scout">...</EntitlementGate>
 */

import type { ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { useEntitlements } from '@/hooks/useEntitlements';
import { ModuleNotEnabled } from '@/components/errors/ModuleNotEnabled';

interface EntitlementGateProps {
  module: 'scheduler' | 'scout';
  children: ReactNode;
}

const MODULE_LABELS: Record<EntitlementGateProps['module'], string> = {
  scheduler: 'Scheduler',
  scout: 'Research Runs',
};

export function EntitlementGate({ module, children }: EntitlementGateProps) {
  const { authResolved } = useAuth();
  const entitlements = useEntitlements();

  // While auth is still revalidating against the server, render a neutral
  // state — a localStorage-restored org may lack entitlements, and showing
  // ModuleNotEnabled here would flash before /auth/me overwrites it.
  if (!authResolved) {
    return null;
  }

  if (!entitlements[module]) {
    return <ModuleNotEnabled moduleName={MODULE_LABELS[module]} />;
  }

  return <>{children}</>;
}
